import { Box } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import DuplicateIcon from "../../../../../../../DesignEditor/components/Icons/DuplicateIcon";
import DeleteIcon from "../../../../../../../assets/images/icons/DeleteIcon";
import { EditIcon2 } from "../../../../../../../assets/images/icons/EditIcon2";
import EyeIcon from "../../../../../../../assets/images/icons/EyeIcon";
import BriaIconButton from "../../../../../../../components/common/BriaIconButton/BriaIconButton";
import ConfirmationPopup from "../../../../../../../components/common/ConfirmationPopup/ConfirmationPopup";
import RouterConstants from "../../../../../../../constants/RouterConstants";
import useSecureNavigate from "../../../../../../../hooks/useSecureNavigate";
import { useAppStore } from "../../../../../../../hooks/useStores";
import { TgDataset } from "../../../../../Datasets/store/tg-dataset-store";
import { isLegacyTgRoute } from "../../../../../utils/tg-utils.ts";
import { EditTgProjectDatasetLocationState } from "../../Edit/EditTgProjectDataset";
import styles from "./TgDatasetActions.module.scss";

type TgDatasetActionsProps = {
	dataset: TgDataset;
	setLoadingRow: (row: { data: TgDataset; column: string } | undefined) => void;
};

const TgDatasetActions = ({ dataset, setLoadingRow }: TgDatasetActionsProps) => {
	const { t } = useTranslation("tg", { keyPrefix: "datasets.dashboard.table.actions" });
	const navigate = useSecureNavigate();
	const { tgStore } = useAppStore();
	const { tgDatasetStore } = tgStore;
	const [openDeletePopup, setOpenDeletePopup] = useState(false);

	const duplicateDataset = async () => {
		setLoadingRow({ data: dataset, column: "actions" });
		try {
			const newDataset = await tgDatasetStore.duplicateDataset(dataset.id);
			const editDatasetState: EditTgProjectDatasetLocationState = {
				datasetToEdit: newDataset,
			};
			navigate(
				`${
					isLegacyTgRoute()
						? RouterConstants.NEW_TG.LEGACY.PROJECTS.fullPath
						: RouterConstants.NEW_TG.PROJECTS.fullPath
				}/${dataset.project_id}/${RouterConstants.NEW_TG.DATASETS.path}/${newDataset.id}/edit`,
				{ state: editDatasetState },
			);
		} finally {
			setLoadingRow(undefined);
		}
	};

	const openDataset = () => {
		const isLegacyRoute = isLegacyTgRoute();
		if (dataset.upload_type === "basic" || !isLegacyRoute) {
			navigate(
				`${
					isLegacyRoute
						? RouterConstants.NEW_TG.LEGACY.PROJECTS.fullPath
						: RouterConstants.NEW_TG.PROJECTS.fullPath
				}/${dataset.project_id}/${RouterConstants.NEW_TG.DATASETS.path}/${dataset.id}`,
			);
		} else if (dataset.upload_type === "advanced" && isLegacyRoute) {
			navigate(
				`${RouterConstants.NEW_TG.LEGACY.PROJECTS.fullPath}/${dataset.project_id}/${RouterConstants.NEW_TG.DATASETS.path}/${dataset.id}/advanced`,
			);
		}
	};

	const editDataset = () => {
		navigate(
			`${
				isLegacyTgRoute()
					? RouterConstants.NEW_TG.LEGACY.PROJECTS.fullPath
					: RouterConstants.NEW_TG.PROJECTS.fullPath
			}/${dataset.project_id}/${RouterConstants.NEW_TG.DATASETS.path}/${dataset.id}/edit`,
		);
	};

	const deleteDataset = async () => {
		try {
			await tgDatasetStore.deleteDataset(dataset.id);
		} finally {
			setOpenDeletePopup(false);
		}
	};

	return (
		<>
			<Box className={styles.actions}>
				{/* Show duplicate only for Basic upload type */}
				{dataset.upload_type === "basic" && dataset.training_version != "fibo" && (
					<BriaIconButton className={styles.iconButton} title={t("duplicate")} onClick={duplicateDataset}>
						<DuplicateIcon />
					</BriaIconButton>
				)}

				{/* Show edit only when status is 'Draft' AND upload_type is 'Basic' */}
				{(dataset.status === "Draft" || !dataset.models?.length) &&
					dataset.upload_type === "basic" &&
					isLegacyTgRoute() && (
						<BriaIconButton className={styles.iconButton} title={t("edit")} onClick={editDataset}>
							<EditIcon2 />
						</BriaIconButton>
					)}

				{/* VIEW button */}
				{
					// View when Completed/advanced without models
					((dataset.status === "Completed" && dataset.upload_type === "advanced") ||
						(dataset.status !== "Draft" && dataset.upload_type === "basic")) && (
						<BriaIconButton className={styles.iconButton} title={t("view")} onClick={openDataset}>
							<EyeIcon sx={{ width: "21px !important" }} />
						</BriaIconButton>
					)
				}

				{/* DELETE button */}
				{
					// Delete when Draft/basic without models
					(((dataset.status === "Draft" || !dataset.models?.length) && dataset.upload_type === "basic") ||
						// Delete when Completed/advanced without models
						(dataset.upload_type === "advanced" && !dataset.models?.length)) && (
						<BriaIconButton
							className={styles.iconButton}
							title={t("delete")}
							onClick={() => setOpenDeletePopup(true)}
						>
							<DeleteIcon />
						</BriaIconButton>
					)
				}
			</Box>
			<ConfirmationPopup
				open={openDeletePopup}
				onClick={deleteDataset}
				loading={tgDatasetStore.loadingDeleteDataset}
				title={t("deletePopup.title")}
				description={<Trans>{t("deletePopup.description")}</Trans>}
				confirmButtonText={t("deletePopup.confirmBtn")}
				onClose={() => setOpenDeletePopup(false)}
			/>
		</>
	);
};

export default observer(TgDatasetActions);
